import { X } from "lucide-react";
import { useMemo, useState, type KeyboardEvent } from "react";

import { useJDSourceText, type JDDetail } from "@/api/jd-queries";
import type { DirectoryMember } from "@/api/queries";
import { Drawer } from "@/components/ui/drawer";
import { managerLabel, memberLabel } from "@/lib/member-label";
import { cn } from "@/lib/utils";

/**
 * Two parts of the Details step — DESIGN §20.17 (audience), §20.11. Member numbers entered as chips,
 * which replaced a comma-separated text box nobody could check by eye, and the client's original text
 * in a drawer, which replaced a block that pushed the whole form down the page.
 */

// core/access.py:_MEMBER_NUMBER — the server refuses anything else, so the chip refuses it first.
const MEMBER_NUMBER = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;

/**
 * Member numbers as chips: a pasted "T1225, T4151" splits, each kept once, anything else refused.
 *
 * With a Workspace `directory` it becomes a picker: type part of a name or number, choose a person,
 * and the chip shows them by name with the manager the API picked (APM, else PM, else PRM). Only the
 * member number is ever stored — `values` stays the list of numbers, so nothing downstream changes.
 * Without a directory (Workspace unreachable, or not configured) it is the plain number entry it was.
 */
export function NumberChips({
  id,
  values,
  onChange,
  placeholder,
  disabled,
  directory,
}: {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  disabled: boolean;
  directory?: DirectoryMember[] | undefined;
}) {
  const [text, setText] = useState("");
  const [bad, setBad] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const byNumber = useMemo(() => new Map((directory ?? []).map((m) => [m.member_no.toLowerCase(), m])), [directory]);
  const q = text.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!directory || !q) return [];
    const held = new Set(values.map((v) => v.toLowerCase()));
    return directory
      .filter((m) => !held.has(m.member_no.toLowerCase()))
      .filter((m) => `${memberLabel(m)} ${m.member_no}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [directory, q, values]);

  function commit(raw: string) {
    const parts = raw.split(/[\s,;]+/).filter(Boolean);
    if (parts.length === 0) return;
    setBad(parts.filter((p) => !MEMBER_NUMBER.test(p)));
    // Once each, matched without case, as core/access.py:split_member_numbers keeps them.
    const next = [...values];
    for (const p of parts) {
      if (MEMBER_NUMBER.test(p) && !next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    }
    onChange(next);
    setText("");
  }

  function pick(m: DirectoryMember) {
    if (!values.some((v) => v.toLowerCase() === m.member_no.toLowerCase())) onChange([...values, m.member_no]);
    setBad([]);
    setText("");
    setOpen(false);
  }

  function key(e: KeyboardEvent<HTMLInputElement>) {
    const hit = suggestions[highlight];
    if (e.key === "ArrowDown" && suggestions.length > 0) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp" && suggestions.length > 0) {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter" && open && hit) {
      e.preventDefault();
      pick(hit);
    } else if (e.key === "Enter" || e.key === "," || (e.key === " " && !directory)) {
      // A space ends a number, but inside a name search it is part of the name.
      e.preventDefault();
      commit(text);
    } else if (e.key === "Backspace" && text === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div className="relative">
      <div
        className={cn(
          "border-bank-border-strong bg-bank-surface flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border px-1.5 py-1",
          "focus-within:border-bank-accent focus-within:ring-bank-accent focus-within:ring-2",
          disabled && "opacity-60",
        )}
      >
        {values.map((v) => {
          const known = byNumber.get(v.toLowerCase());
          const manager = known ? managerLabel(known) : "";
          return (
            <span
              key={v}
              title={known ? `${memberLabel(known)} · #${v}` : v}
              className="bg-bank-accent-wash text-bank-accent-ink inline-flex min-h-6 items-center gap-1 rounded py-0.5 pr-1 pl-2 text-xs"
            >
              {known ? (
                <span className="flex flex-col leading-tight">
                  <span>{memberLabel(known)}</span>
                  {manager && <span className="text-bank-muted text-[10.5px]">{manager}</span>}
                </span>
              ) : (
                <span className="font-mono">{v}</span>
              )}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${known ? memberLabel(known) : v}`}
                  onClick={() => onChange(values.filter((x) => x !== v))}
                  className="hover:bg-bank-surface grid size-4 place-items-center rounded"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              )}
            </span>
          );
        })}
        <input
          id={id}
          disabled={disabled}
          value={text}
          role={directory ? "combobox" : undefined}
          aria-expanded={directory ? open && suggestions.length > 0 : undefined}
          aria-autocomplete={directory ? "list" : undefined}
          autoComplete="off"
          placeholder={values.length ? "" : directory ? "Search by name or member number…" : placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onKeyDown={key}
          onBlur={() => {
            setOpen(false);
            // With a directory, half a name ("Dip") must not be saved as a member: only a known number counts.
            if (!directory || byNumber.has(text.trim().toLowerCase())) commit(text);
            else setText("");
          }}
          onPaste={(e) => {
            e.preventDefault();
            commit(e.clipboardData.getData("text"));
          }}
          className="placeholder:text-bank-muted min-w-40 flex-1 bg-transparent px-1 text-[13px] outline-none"
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className="bg-bank-surface border-bank-border-strong absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border py-1 shadow-md"
        >
          {suggestions.map((m, i) => (
            <li
              key={m.member_no}
              role="option"
              aria-selected={i === highlight}
              onClick={() => pick(m)}
              onMouseEnter={() => setHighlight(i)}
              className={cn("cursor-pointer px-2.5 py-1.5", i === highlight && "bg-bank-accent-wash text-bank-accent-ink")}
            >
              <div className="text-sm">
                {memberLabel(m)} <span className="text-bank-muted font-mono text-[11px]">#{m.member_no}</span>
              </div>
              {managerLabel(m) && <div className="text-bank-muted text-[11px]">{managerLabel(m)}</div>}
            </li>
          ))}
        </ul>
      )}
      {bad.length > 0 && (
        <p className="text-bank-attention-ink mt-1 text-xs">
          Not a member number, left out: {bad.join(", ")}.
        </p>
      )}
    </div>
  );
}

export function SourceTextDrawer({
  jd,
  open,
  onClose,
}: {
  jd: JDDetail;
  open: boolean;
  onClose: () => void;
}) {
  const text = useJDSourceText(jd.source.id, open);
  return (
    <Drawer open={open} onClose={onClose} title={jd.source.filename ?? "The client's text"}>
      <pre className="p-4 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap">
        {text.data?.raw_text ?? (text.error ? "Could not load the text." : "Loading…")}
      </pre>
    </Drawer>
  );
}
